import React, { useEffect, useState } from "react";
import CrudPage from "../components/CrudPage";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { UserPlus, KeyRound, UserCheck, UserX, Mail, History } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const genPassword = () => {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let p = ""; for (let i=0;i<10;i++) p += chars[Math.floor(Math.random()*chars.length)];
  return p;
};
const genUsername = (fn, ln) => `${(fn||"").toLowerCase().replace(/[^a-z]/g,"")}.${(ln||"").toLowerCase().replace(/[^a-z]/g,"")}`.replace(/^\.|\.$/g,"");

function formatLastLogin(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return null; }
}

function AccountBadge({ account }) {
  const st = account?.status || "none";
  if (st === "active") return <span data-testid="account-badge-active" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-num font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/40 rounded px-1.5 py-0.5">● Attivo</span>;
  if (st === "disabled") return <span data-testid="account-badge-disabled" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-num font-bold text-rose-400 bg-rose-500/10 border border-rose-500/40 rounded px-1.5 py-0.5">● Disattivato</span>;
  return <span data-testid="account-badge-none" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-num text-slate-400 bg-slate-800/40 border border-slate-700 rounded px-1.5 py-0.5">— Nessun account</span>;
}

function LastLoginBadge({ account }) {
  const st = account?.status || "none";
  if (st === "none") return null;
  const formatted = formatLastLogin(account?.last_login_at);
  return (
    <span
      data-testid="account-last-login"
      title={formatted ? `Ultimo accesso: ${formatted}` : "Nessun accesso registrato"}
      className={`inline-flex items-center gap-1 text-[10px] font-mono-num rounded px-1.5 py-0.5 border ${formatted ? "text-sky-300 bg-sky-500/10 border-sky-500/30" : "text-slate-500 bg-slate-800/40 border-slate-700"}`}
    >
      {formatted ? `⏱ ${formatted}` : "⏱ Mai"}
    </span>
  );
}

function AccountActions({ row, onChanged }) {
  const [mode, setMode] = useState(null); // 'create' | 'reset' | 'edit'
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const openCreate = () => {
    setMode("create");
    setEmail(row.email || "");
    setUsername(genUsername(row.first_name, row.last_name));
    setPassword(genPassword());
  };
  const openReset = () => { setMode("reset"); setPassword(genPassword()); };
  const openEdit = () => {
    setMode("edit");
    setEmail(row.account?.email || "");
    setUsername(row.account?.username || "");
  };
  const close = () => { setMode(null); setEmail(""); setUsername(""); setPassword(""); };

  const doCreate = async () => {
    setBusy(true);
    try {
      await api.post(`/drivers/${row.id}/account`, { email, password, username });
      toast.success("Account creato");
      close(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const doReset = async () => {
    setBusy(true);
    try {
      await api.post(`/drivers/${row.id}/reset-login-password`, { password });
      toast.success("Password resettata");
      close();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const doEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/drivers/${row.id}/account`, { email, name: username });
      toast.success("Account aggiornato");
      close(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const toggleActive = async () => {
    const activate = row.account?.status !== "active";
    if (!confirm(activate ? "Riattivare l'account?" : "Disattivare l'account? Il driver non potrà più accedere ma i suoi dati storici restano invariati.")) return;
    try {
      await api.patch(`/drivers/${row.id}/account`, { active: activate });
      toast.success(activate ? "Account attivato" : "Account disattivato");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const st = row.account?.status || "none";
  return (
    <>
      {st === "none" ? (
        <button onClick={openCreate} data-testid={`create-account-${row.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs font-mono-num font-bold">
          <UserPlus size={12}/>+ Crea account
        </button>
      ) : (
        <div className="flex items-center gap-1 flex-wrap">
          <button onClick={openEdit} data-testid={`edit-account-${row.id}`} title="Modifica" className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-sky-400"><Mail size={13}/></button>
          <button onClick={openReset} data-testid={`reset-account-${row.id}`} title="Reset password" className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400"><KeyRound size={13}/></button>
          <button onClick={toggleActive} data-testid={`toggle-account-${row.id}`} title={st === "active" ? "Disattiva" : "Riattiva"} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400">
            {st === "active" ? <UserX size={13}/> : <UserCheck size={13}/>}
          </button>
        </div>
      )}

      <Dialog open={mode !== null} onOpenChange={(o)=>!o&&close()}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {mode === "create" ? "Crea account Driver" : mode === "reset" ? "Reset password" : "Modifica account"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">Autista</div>
              <div className="text-white font-display font-extrabold text-base">{row.first_name} {row.last_name}</div>
              <div className="text-[10px] text-slate-400 font-mono-num mt-0.5">Ruolo account: DRIVER · permessi limitati</div>
            </div>
            {(mode === "create" || mode === "edit") && (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Email *</Label>
                  <Input data-testid="account-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} className="bg-slate-900 border-slate-800"/>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Username</Label>
                  <Input data-testid="account-username" value={username} onChange={e=>setUsername(e.target.value)} className="bg-slate-900 border-slate-800"/>
                </div>
              </>
            )}
            {(mode === "create" || mode === "reset") && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Password {mode === "create" ? "temporanea" : "nuova"} *</Label>
                <div className="flex gap-2">
                  <Input data-testid="account-password" value={password} onChange={e=>setPassword(e.target.value)} className="bg-slate-900 border-slate-800 font-mono-num"/>
                  <Button type="button" onClick={()=>setPassword(genPassword())} variant="outline" className="border-slate-800 bg-transparent text-slate-300">🎲</Button>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Comunicala all'autista in modo sicuro. Potrà cambiarla al primo accesso.</div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={close} className="border-slate-800 bg-transparent text-slate-300">Annulla</Button>
              <Button data-testid="account-save" disabled={busy} onClick={mode === "create" ? doCreate : mode === "reset" ? doReset : doEdit} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
                {mode === "create" ? "Crea account" : mode === "reset" ? "Reset password" : "Salva"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AutistiPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => { api.get("/vehicles").then(r => setVehicles(r.data || [])).catch(() => {}); }, [reloadKey]);
  const vehiclesOf = (id) => vehicles.filter(v => (v.assigned_driver_ids?.length ? v.assigned_driver_ids : (v.current_driver_id ? [v.current_driver_id] : [])).includes(id));
  const bump = () => setReloadKey(k=>k+1);
  return (
    <CrudPage
      key={reloadKey}
      title="Autisti" endpoint="/drivers" testidPrefix="autisti"
      fields={[
        { key: "first_name", label: "Nome" },
        { key: "last_name", label: "Cognome" },
        { key: "phone", label: "Telefono" },
        { key: "email", label: "Email", type: "email" },
        { key: "contract_type", label: "Tipo rapporto", type: "select", options: [{value:"dipendente",label:"Dipendente"},{value:"partita_iva",label:"Partita IVA"},{value:"occasionale",label:"Occasionale"}] },
        { key: "document", label: "Documento" },
        { key: "license_number", label: "Patente" },
        { key: "license_expiry", label: "Scadenza patente", type: "date" },
        { key: "doc_expiry", label: "Scadenza documento", type: "date" },
        { key: "status", label: "Stato", type: "select", options: [
          {value:"disponibile",label:"Disponibile"},{value:"occupato",label:"Occupato"},
          {value:"non_disponibile",label:"Non disponibile"},{value:"ferie",label:"Ferie"},{value:"malattia",label:"Malattia"}
        ], default: "disponibile" },
        { key: "notes", label: "Note", type: "textarea", fullWidth: true },
      ]}
      columns={[
        { key: "first_name", label: "Nome", render: (r) => `${r.first_name} ${r.last_name}` },
        { key: "storico", label: "Storico", exportValue: () => "", render: (r) => <Link to={`/autisti/${r.id}/storico`} onClick={(e) => e.stopPropagation()} data-testid={`storico-${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs font-bold"><History size={13} />Storico</Link> },
        { key: "phone", label: "Telefono" },
        { key: "email", label: "Email" },
        { key: "contract_type", label: "Rapporto" },
        { key: "license_expiry", label: "Scad. Patente" },
        { key: "status", label: "Stato", render: (r) => <StatusBadge status={r.status}/> },
        { key: "assigned_vehicles", label: "Veicoli", exportValue: (r) => vehiclesOf(r.id).map(v => v.plate).join(" / "), render: (r) => {
          const list = vehiclesOf(r.id);
          return list.length ? (
            <div className="flex flex-wrap gap-1" data-testid={`driver-vehicles-${r.id}`}>
              {list.map(v => <span key={v.id} className="rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-100 text-[11px] px-2 py-0.5 whitespace-nowrap">{v.plate || "—"} · {[v.brand, v.model].filter(Boolean).join(" ")}</span>)}
            </div>
          ) : <span className="text-slate-500 italic text-xs" data-testid={`driver-vehicles-${r.id}`}>Nessuno</span>;
        } },
        { key: "account", label: "Account", render: (r) => (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <AccountBadge account={r.account}/>
              <AccountActions row={r} onChanged={bump}/>
            </div>
            <LastLoginBadge account={r.account}/>
          </div>
        )},
      ]}
    />
  );
}
